import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '@pbt/pbt-ui-components'

import type { PuiCKEditorHandle } from '~/components/common/ckeditor/PuiCKEditor'

import { useTranslateDischargeNotes } from './hooks/useTranslateDischargeNotes'

import styles from './ShareLanguageDropdown.module.scss'

/** Language codes supported by the translation dropdown */
const LANGUAGE_CODES = ['en-US', 'es-US', 'fr-FR'] as const

interface ShareLanguageDropdownProps {
  editorRef?: React.RefObject<PuiCKEditorHandle | null>
}

export const ShareLanguageDropdown: React.FC<ShareLanguageDropdownProps> = ({
  editorRef,
}) => {
  const { t } = useTranslation()
  const { translateNotes, isTranslating } =
    useTranslateDischargeNotes(editorRef)
  const [selectedLanguage, setSelectedLanguage] = useState('en-US')

  // Guard against rapid language switches while a translation is in-flight
  const isTranslatingRef = useRef(false)

  // Explicit t() calls so i18next-parser can statically detect each key
  const labelByCode: Record<string, string> = useMemo(
    () => ({
      'en-US': t('Common:SHARE_LANGUAGE.ENGLISH'),
      'es-US': t('Common:SHARE_LANGUAGE.SPANISH'),
      'fr-FR': t('Common:SHARE_LANGUAGE.FRENCH'),
    }),
    [t],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.target.value

      if (!newValue || newValue === selectedLanguage) return

      // Prevent rapid successive calls (ref catches window before React re-render)
      if (isTranslatingRef.current) return

      const previousLanguage = selectedLanguage
      setSelectedLanguage(newValue)
      isTranslatingRef.current = true

      translateNotes(newValue)
        .then((success) => {
          if (!success) {
            // Roll back to previous language on failure
            setSelectedLanguage(previousLanguage)
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console -- intentional: log unexpected errors for debugging
          console.error('[ShareLanguageDropdown] translateNotes error:', err)
          setSelectedLanguage(previousLanguage)
        })
        .finally(() => {
          isTranslatingRef.current = false
        })
    },
    [translateNotes, selectedLanguage],
  )

  const displayLabel = isTranslating
    ? t('Common:SHARE_LANGUAGE.TRANSLATING')
    : t('Common:SHARE_LANGUAGE.SHARE_IN')

  return (
    <div className={styles.root}>
      <Text variant="lowAccent3">{displayLabel}</Text>
      <select
        aria-label={t('Common:SHARE_LANGUAGE.ARIA_LABEL')}
        className={styles.select}
        data-testid="language-select"
        disabled={isTranslating}
        value={selectedLanguage}
        onChange={handleChange}
      >
        {LANGUAGE_CODES.map((code) => (
          <option key={code} value={code}>
            {labelByCode[code]}
          </option>
        ))}
      </select>
    </div>
  )
}
